import React, { useEffect, useRef, useState, useCallback } from 'react';

/* ═══════════════════════════════════════════════════
   TERMINAL-INSPIRED SHARED EFFECT COMPONENTS
   ═══════════════════════════════════════════════════ */

// Mouse-following ambient glow (page-level)
export const MouseGlow = () => {
  const [pos, setPos] = useState({ x: -200, y: -200 });
  useEffect(() => {
    const h = (e) => setPos({ x: e.clientX, y: e.clientY });
    window.addEventListener('mousemove', h);
    return () => window.removeEventListener('mousemove', h);
  }, []);
  return (
    <div className="fixed inset-0 pointer-events-none z-[1]"
      style={{ background: `radial-gradient(700px circle at ${pos.x}px ${pos.y}px, rgba(0,255,209,0.12), transparent 60%)` }} />
  );
};

// Film grain overlay
export const GrainOverlay = () => (
  <div className="fixed inset-0 pointer-events-none z-[2] opacity-[0.03] mix-blend-overlay"
    style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")` }} />
);

// SVG Notch Separator
export const NotchSeparator = ({ bgTo = '#2A2A2A' }) => (
  <div className="relative w-full overflow-hidden" style={{ height: '52px' }}>
    <svg viewBox="0 0 1440 52" preserveAspectRatio="none" className="absolute inset-0 w-full h-full block">
      <path d="M0,0 L420,0 Q450,0 460,14 L482,38 Q492,52 512,52 L928,52 Q948,52 958,38 L980,14 Q990,0 1020,0 L1440,0 L1440,52 L0,52 Z" fill={bgTo} />
      <path d="M0,0 L420,0 Q450,0 460,14 L482,38 Q492,52 512,52 L928,52 Q948,52 958,38 L980,14 Q990,0 1020,0 L1440,0" fill="none" stroke="rgba(0,255,209,0.2)" strokeWidth="1.5" />
    </svg>
  </div>
);

// Circuit path background with scroll-driven draw
export const CircuitPaths = () => {
  const ref = useRef(null);
  const [p, setP] = useState(0);
  useEffect(() => {
    const handler = () => {
      if (!ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      setP(Math.max(0, Math.min(1, (window.innerHeight - rect.top) / (window.innerHeight + rect.height))));
    };
    window.addEventListener('scroll', handler, { passive: true });
    handler();
    return () => window.removeEventListener('scroll', handler);
  }, []);
  const L = 2000;
  return (
    <div ref={ref} className="absolute inset-0 pointer-events-none overflow-hidden">
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 1440 900" preserveAspectRatio="none">
        <path d="M-50,150 Q300,80 500,300 T900,250 Q1100,200 1490,400" fill="none" stroke="rgba(0,255,209,0.04)" strokeWidth="1" />
        <path d="M-50,150 Q300,80 500,300 T900,250 Q1100,200 1490,400" fill="none" stroke="#00FFD1" strokeWidth="1.5" strokeLinecap="round"
          style={{ strokeDasharray: L, strokeDashoffset: L - p * L, opacity: 0.2 }} />
        <path d="M1490,700 Q1100,750 900,550 T400,650 Q200,700 -50,500" fill="none" stroke="rgba(196,149,106,0.03)" strokeWidth="1" />
        <path d="M1490,700 Q1100,750 900,550 T400,650 Q200,700 -50,500" fill="none" stroke="#C4956A" strokeWidth="1.5" strokeLinecap="round"
          style={{ strokeDasharray: L, strokeDashoffset: L - p * L * 0.7, opacity: 0.15 }} />
        <circle cx={200 + p * 900} cy={200 + Math.sin(p * 3.14) * 100} r="4" fill="#00FFD1" opacity={p > 0.05 ? 0.5 : 0}>
          <animate attributeName="r" values="3;6;3" dur="2s" repeatCount="indefinite" />
        </circle>
        <circle cx={1200 - p * 800} cy={600 - Math.sin(p * 3.14) * 100} r="4" fill="#C4956A" opacity={p > 0.1 ? 0.4 : 0}>
          <animate attributeName="r" values="2;5;2" dur="2.5s" repeatCount="indefinite" />
        </circle>
      </svg>
    </div>
  );
};

// Character-by-character reveal on scroll (words stay together)
export const CharReveal = ({ text, className = '', tag = 'h2', delay = 0 }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } }, { threshold: 0.15 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  const Tag = tag;
  const words = text.split(' ');
  let charIndex = 0;
  return (
    <Tag ref={ref} className={className} aria-label={text}>
      {words.map((word, wi) => {
        const chars = word.split('');
        const wordEl = (
          <span key={`w-${wi}`} className="inline-block whitespace-nowrap">
            {chars.map((char, ci) => {
              const idx = charIndex++;
              return (
                <span key={`c-${idx}`} aria-hidden="true"
                  className="inline-block"
                  style={{
                    transition: `opacity 0.6s ease ${delay + idx * 22}ms, transform 0.6s cubic-bezier(0.16,1,0.3,1) ${delay + idx * 22}ms`,
                    opacity: visible ? 1 : 0,
                    transform: visible ? 'translateY(0) rotate(0)' : 'translateY(20px) rotate(2deg)',
                  }}>
                  {char}
                </span>
              );
            })}
          </span>
        );
        charIndex++; // count the space
        return (
          <React.Fragment key={`wg-${wi}`}>
            {wordEl}
            {wi < words.length - 1 && <span className="inline-block">&nbsp;</span>}
          </React.Fragment>
        );
      })}
    </Tag>
  );
};

// 3D Tilt card with cross decorators
export const TiltCard = ({ children, className = '', accentColor = '#00FFD1' }) => {
  const ref = useRef(null);
  const [style, setStyle] = useState({});
  const handleMove = useCallback((e) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    setStyle({
      transform: `perspective(800px) rotateY(${x * 8}deg) rotateX(${-y * 8}deg) scale(1.02)`,
      boxShadow: `${-x * 20}px ${y * 20}px 40px rgba(0,0,0,0.3), 0 0 20px ${accentColor}15`,
    });
  }, [accentColor]);
  const handleLeave = () => setStyle({ transform: 'perspective(800px) rotateY(0) rotateX(0) scale(1)', boxShadow: 'none' });

  return (
    <div ref={ref} className={`relative transition-all duration-300 ${className}`}
      style={style} onMouseMove={handleMove} onMouseLeave={handleLeave}>
      <div className="absolute -inset-[1px] rounded-2xl opacity-0 hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        style={{ background: `linear-gradient(135deg, ${accentColor}40, transparent 40%, transparent 60%, ${accentColor}20)`, borderRadius: 'inherit' }} />
      <div className="absolute top-2 right-2 w-3 h-3 opacity-20">
        <div className="absolute top-1/2 left-0 w-full h-px animate-cross-h" style={{ backgroundColor: accentColor }} />
        <div className="absolute left-1/2 top-0 h-full w-px animate-cross-v" style={{ backgroundColor: accentColor }} />
      </div>
      <div className="absolute bottom-2 left-2 w-3 h-3 opacity-20">
        <div className="absolute top-1/2 left-0 w-full h-px animate-cross-h" style={{ backgroundColor: accentColor }} />
        <div className="absolute left-1/2 top-0 h-full w-px animate-cross-v" style={{ backgroundColor: accentColor }} />
      </div>
      {children}
    </div>
  );
};

// Scroll-driven text interstitial (word-by-word dramatic reveal)
export const TextInterstitial = ({ lines }) => {
  const ref = useRef(null);
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const handler = () => {
      if (!ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      const vh = window.innerHeight;
      setProgress(Math.max(0, Math.min(1, (vh - rect.top) / (vh + rect.height))));
    };
    window.addEventListener('scroll', handler, { passive: true });
    handler();
    return () => window.removeEventListener('scroll', handler);
  }, []);

  const allWords = [];
  lines.forEach((line, li) => {
    line.split(' ').forEach((word) => { allWords.push({ word, lineIndex: li }); });
  });
  const total = allWords.length;

  return (
    <div ref={ref} className="relative py-40 lg:py-56 bg-[#0a0a0a] overflow-hidden">
      <div className="absolute inset-0" style={{
        background: `radial-gradient(ellipse at ${30 + progress * 40}% 50%, rgba(0,255,209,0.1) 0%, transparent 50%)`,
      }} />
      <div className="absolute inset-0 pointer-events-none opacity-[0.03]" style={{
        backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,255,209,0.1) 2px, rgba(0,255,209,0.1) 4px)',
      }} />
      <div className="max-w-[1200px] mx-auto px-6 lg:px-8 text-center">
        <div className="flex flex-wrap justify-center gap-x-[0.35em] gap-y-2">
          {allWords.map((item, i) => {
            const ws = (i / total) * 0.7;
            const wp = Math.max(0, Math.min(1, (progress - ws) * 4));
            const isNewLine = i > 0 && item.lineIndex !== allWords[i - 1].lineIndex;
            return (
              <React.Fragment key={`word-${i}`}>
                {isNewLine && <div className="w-full h-0" />}
                <span className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl xl:text-8xl font-bold leading-[1.1] inline-block"
                  style={{
                    fontFamily: "'Outfit', sans-serif",
                    letterSpacing: '-0.03em',
                    opacity: wp,
                    transform: `translateY(${(1 - wp) * 60}px) scale(${0.8 + wp * 0.2})`,
                    filter: `blur(${(1 - wp) * 8}px)`,
                    color: item.lineIndex % 2 === 0 ? '#F5F0E0' : '#00FFD1',
                    textShadow: wp > 0.8 && item.lineIndex % 2 === 1 ? '0 0 40px rgba(0,255,209,0.3)' : 'none',
                  }}>
                  {item.word}
                </span>
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Scroll indicator
export const ScrollIndicator = () => {
  const [op, setOp] = useState(1);
  useEffect(() => {
    const h = () => setOp(Math.max(0, 1 - window.scrollY / 300));
    window.addEventListener('scroll', h, { passive: true });
    return () => window.removeEventListener('scroll', h);
  }, []);
  return (
    <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 z-30" style={{ opacity: op }}>
      <div className="w-6 h-10 rounded-full border-2 border-white/20 flex justify-center pt-2">
        <div className="w-1.5 h-2.5 rounded-full bg-[#00FFD1] animate-scroll-dot" />
      </div>
      <p className="text-[10px] uppercase tracking-[0.3em] text-white/40 font-mono">Scroll to explore</p>
    </div>
  );
};

// Reveal on scroll (direction-aware)
export const Reveal = ({ children, className = '', delay = 0, direction = 'up' }) => {
  const ref = useRef(null);
  const [v, setV] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setV(true); obs.disconnect(); } }, { threshold: 0.1, rootMargin: '0px 0px -60px 0px' });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  const transforms = { up: 'translateY(50px)', down: 'translateY(-50px)', left: 'translateX(50px)', right: 'translateX(-50px)', scale: 'scale(0.9)' };
  return (
    <div ref={ref} className={className}
      style={{ transition: `opacity 0.8s cubic-bezier(0.16,1,0.3,1) ${delay}ms, transform 0.8s cubic-bezier(0.16,1,0.3,1) ${delay}ms`,
        opacity: v ? 1 : 0, transform: v ? 'translateY(0) scale(1)' : transforms[direction] || transforms.up }}>
      {children}
    </div>
  );
};

// Global Terminal CSS keyframes (inject once)
export const TerminalStyles = () => (
  <style>{`
    @keyframes scroll-dot { 0% { transform: translateY(0); opacity: 1; } 60% { transform: translateY(10px); opacity: 0; } 100% { transform: translateY(0); opacity: 0; } }
    .animate-scroll-dot { animation: scroll-dot 1.8s ease-in-out infinite; }
    @keyframes cross-h { 0%, 100% { transform: scaleX(1); opacity: 0.3; } 50% { transform: scaleX(0.2); opacity: 0.8; } }
    @keyframes cross-v { 0%, 100% { transform: scaleY(1); opacity: 0.3; } 50% { transform: scaleY(0.2); opacity: 0.8; } }
    .animate-cross-h { animation: cross-h 4s ease-in-out infinite; }
    .animate-cross-v { animation: cross-v 4s ease-in-out infinite 1s; }
    @keyframes cta-glow {
      0%, 100% { box-shadow: 0 0 20px rgba(0,255,209,0.3), 0 0 60px rgba(0,255,209,0.1); }
      50% { box-shadow: 0 0 30px rgba(0,255,209,0.5), 0 0 80px rgba(0,255,209,0.2), 0 0 120px rgba(0,255,209,0.1); }
    }
    .animate-cta-glow { animation: cta-glow 2s ease-in-out infinite; }
  `}</style>
);
